import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TOAST_MS } from "@/components/Toast";
import { SCREEN_PATHS } from "@/constants/screens";

const ROUTE_PROFILE_PATH = "/admin?tab=route";

// Notification type -> screen it concerns; unknown types stay unlinked.
const TYPE_PATHS = {
  DB_BACKUP_FAILED: "/admin?tab=dbbackup",
  FILE_BACKUP_FAILED: "/admin?tab=dbbackup",
  WORK_REVISION_REQUESTED: SCREEN_PATHS.planning,
  RECEIVING_DISCREPANCY: SCREEN_PATHS.planning,
  DAMAGE_REPORTED: SCREEN_PATHS.planning,
  BOX_OVER_PLAN: SCREEN_PATHS.planning,
  INBOUND_DAMAGED: SCREEN_PATHS.planning,
  INBOUND_UNANNOUNCED: SCREEN_PATHS.planning,
  BOX_READY: SCREEN_PATHS.marketplace_amazon_fba,
  WORK_COMPLETED: SCREEN_PATHS.marketplace_amazon_fba,
  LABELS_READY: SCREEN_PATHS.receiver_warehouse,
  PALLET_READY: SCREEN_PATHS.planning,
  PALLET_SHIPPED: SCREEN_PATHS.planning,
  PALLET_RECEIVED: SCREEN_PATHS.planning,
  PALLET_INCOMING: SCREEN_PATHS.receiver_warehouse,
  PLANNING_SUBMITTED: SCREEN_PATHS.receiver_warehouse,
  ROUTE_PROFILE_CREATED: ROUTE_PROFILE_PATH,
  ROUTE_PROFILE_UPDATED: ROUTE_PROFILE_PATH,
  ROUTE_PROFILE_ACTIVATED: ROUTE_PROFILE_PATH,
  ROUTE_PROFILE_DEACTIVATED: ROUTE_PROFILE_PATH,
};

function useNotificationText() {
  const { t } = useLang();
  return (n) => {
    const key = `notification.type.${n.type}`;
    const text = t(key, n.data || {});
    return text === key ? t("notification.type.unknown") : text;
  };
}

export function NotificationBell({ center, className = "" }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const textOf = useNotificationText();
  const { items, unreadCount, markRead, markAllRead } = center;

  const openItem = async (n) => {
    setOpen(false);
    try { if (!n.read_at) await markRead(n.id); } catch {}
    if (TYPE_PATHS[n.type]) navigate(TYPE_PATHS[n.type]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={t("notification.center")} data-testid="notification-bell"
          className={`relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95 transition-all ${className}`}>
          <Bell className="w-5 h-5 text-slate-700 dark:text-slate-300" />
          {unreadCount > 0 && (
            <span data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
          <span className="text-sm font-semibold">{t("notification.center")}</span>
          <button type="button" disabled={unreadCount === 0} onClick={() => markAllRead().catch(() => {})}
            className="text-xs font-semibold text-blue-600 disabled:text-slate-400 disabled:cursor-default">
            {t("notification.mark_all_read")}
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate-500">{t("notification.empty")}</p>
          )}
          {items.map(n => (
            <button key={n.id} type="button" onClick={() => openItem(n)}
              className="w-full text-left px-3 py-2.5 flex gap-2 border-b border-slate-50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800">
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read_at ? "bg-transparent" : "bg-blue-600"}`} />
              <span className="min-w-0">
                <span className={`block text-sm ${n.read_at ? "text-slate-600 dark:text-slate-400" : "font-semibold"}`}>
                  {textOf(n)}
                </span>
                <span className="block text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Announces newly arrived rows in the shared toast host; the bell keeps them until they are read. */
export function NotificationToaster({ center }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const textOf = useNotificationText();
  const { arrived, markRead } = center;

  useEffect(() => {
    if (!arrived || arrived.length === 0) return;
    const icon = <Bell className="w-4 h-4" />;
    if (arrived.length > 1) {
      toast(t("notification.many", { count: arrived.length }), { id: "notification-many", icon, duration: TOAST_MS.notification });
      return;
    }
    const n = arrived[0];
    const path = TYPE_PATHS[n.type];
    const open = async () => {
      try { await markRead(n.id); } catch {}
      navigate(path);
    };
    toast(textOf(n), {
      id: n.id, icon, duration: TOAST_MS.notification,
      action: path ? { label: t("notification.open"), onClick: open } : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrived]);

  return null;
}
